<template>
  <div class="workbench-page">
    <CommonHeader />
    <div class="workbench-container">
      <div class="workbench-card">
        <div class="workbench-header">
          <div class="workbench-header-left">
            <div class="title-icon-wrap">
              <el-icon class="title-icon">
                <Monitor />
              </el-icon>
            </div>
            <div class="title-block">
              <div class="title-row">
                <h2 class="workbench-title">
                  <span
                    v-if="channelOrOrganizationName"
                    class="channel-org-name-prefix"
                  >{{ channelOrOrganizationName }}</span>
                  <span
                    v-if="channelOrOrganizationName"
                    class="title-separator"
                  >·</span>
                  工作台
                </h2>
                <span
                  v-if="rolePrefix"
                  class="role-badge"
                >{{ rolePrefix }}</span>
              </div>
              <p class="workbench-subtitle">
                管理您的用户与业务
              </p>
            </div>
          </div>
          <div class="workbench-header-actions">
            <el-button
              v-if="$auth.isChannel() && showUserManagementTab"
              type="primary"
              class="header-action-btn"
              @click="handleChannelCodeModify"
            >
              <el-icon class="btn-icon">
                <EditPen />
              </el-icon>
              <span>更新渠道码</span>
            </el-button>
            <el-button
              v-if="$auth.isSalesman() && !isWorkbenchDisabled"
              type="default"
              class="header-action-btn unbind-btn"
              @click="handleUnbind"
            >
              <el-icon class="btn-icon">
                <Link />
              </el-icon>
              <span>解绑业务员身份</span>
            </el-button>
          </div>
        </div>

        <!-- 当前为业务员且已被停用时，不展示工作台 tab，仅提示 -->
        <div
          v-if="isWorkbenchDisabled"
          class="workbench-disabled-tip"
        >
          <div class="disabled-tip-icon">
            <el-icon :size="56">
              <WarningFilled />
            </el-icon>
          </div>
          <h3 class="disabled-tip-title">
            当前账号工作台操作不可用
          </h3>
          <p class="disabled-tip-desc">
            您的业务员账号无法使用工作台相关功能。如有疑问请联系机构管理员。
          </p>
        </div>
        <el-tabs
          v-else
          v-model="activeTab"
          class="workbench-tabs"
        >
          <el-tab-pane
            v-if="showSubChannelManagementTab"
            label="子渠道管理"
            name="subChannelManagement"
          >
            <SubChannelManagement />
          </el-tab-pane>
          <el-tab-pane
            v-if="showUserManagementTab"
            label="用户管理"
            name="userManagement"
          >
            <UserManagement />
          </el-tab-pane>
          <el-tab-pane
            v-if="$auth.hasSalesmanManagementPermission()"
            label="业务员管理"
            name="salesmanManagement"
          >
            <SalesmanManagement />
          </el-tab-pane>
          <el-tab-pane
            v-if="showDataStatisticsTab"
            label="数据统计"
            name="otherManagement"
            lazy
          >
            <PrimaryChannelDataStatistics v-if="isPrimaryChannel" />
            <NormalChannelDataStatistics v-else />
          </el-tab-pane>
        </el-tabs>
      </div>
    </div>
    <CommonFooter />
    <ChannelCodeModify
      v-model="channelCodeDialogVisible"
      @success="handleChannelCodeModifySuccess"
    />
  </div>
</template>

<script>
import CommonHeader from '@/views/commonHeaderAndFooter/header.vue'
import CommonFooter from '@/views/commonHeaderAndFooter/footer.vue'
import SubChannelManagement from './subChannel/SubChannelManagement.vue'
import UserManagement from './UserManagement.vue'
import SalesmanManagement from './salesman/SalesmanManagement.vue'
import NormalChannelDataStatistics from './dataStatistics/normalChannelDataStatistics/NormalChannelDataStatistics.vue'
import PrimaryChannelDataStatistics from './dataStatistics/primaryChannelDataStatistics/PrimaryChannelDataStatistics.vue'
import ChannelCodeModify from './ChannelCodeModify.vue'
import { isGovernancePrimaryChannel } from '@/enums/ChannelTypeEnum'
import { WarningFilled, Monitor, EditPen, Link } from '@element-plus/icons-vue'
import { salesmanUnBundling } from '@/api/workbench'
import { showEnhancedConfirmDialog } from '@/utils/messageUtils'
import { ElMessage } from 'element-plus'

export default {
  name: 'Workbench',
  components: {
    CommonHeader,
    CommonFooter,
    SubChannelManagement,
    UserManagement,
    SalesmanManagement,
    NormalChannelDataStatistics,
    PrimaryChannelDataStatistics,
    ChannelCodeModify,
    WarningFilled,
    Monitor,
    EditPen,
    Link
  },
  data() {
    return {
      activeTab: 'subChannelManagement',
      // 渠道码修改对话框显示状态
      channelCodeDialogVisible: false
    }
  },
  computed: {
    /**
     * 根据当前用户角色获取工作台标题前缀
     * @returns {string} 角色前缀（机构、业务员）
     */
    rolePrefix() {
      if (this.$auth.isChannel()) {
        return '渠道'
      }else if (this.$auth.isOrganization()) {
        return '机构'
      }
      return '业务员'
    },
    /** 机构名称 / 渠道名称（登录主体接口返回） */
    channelOrOrganizationName() {
      const mb = this.$auth.getMainBody()
      return mb && mb.channelOrOrganizationName ? String(mb.channelOrOrganizationName).trim() : ''
    },
    /**
     * 当前为业务员且已被停用时，工作台 tab 不展示，仅显示停用提示
     */
    isWorkbenchDisabled() {
      if(this.$auth.isOrganization()) return false
      if(this.$auth.isChannel()) return false
      return !this.$auth.isSalesmanNotDisabled()
    },
    /**
     * 专项治理一级渠道：展示子渠道管理。
     * 专项治理子渠道 / 版权产业渠道：不展示子渠道管理。
     */
    showSubChannelManagementTab() {
      if (!this.$auth.isChannel()) return false
      const mb = this.$auth.getMainBody()
      return isGovernancePrimaryChannel(mb)
    },
    showUserManagementTab() {
      if (!this.$auth.isChannel()) return true
      const mb = this.$auth.getMainBody()
      return !isGovernancePrimaryChannel(mb)
    },
    /** 是否专项治理一级渠道 */
    isPrimaryChannel() {
      if (!this.$auth.isChannel()) return false
      return isGovernancePrimaryChannel(this.$auth.getMainBody())
    },
    /** 渠道身份才展示数据统计 */
    showDataStatisticsTab() {
      return this.$auth.isChannel()
    }
  },
  watch: {
    showSubChannelManagementTab: 'syncWorkbenchActiveTab',
    showUserManagementTab: 'syncWorkbenchActiveTab'
  },
  mounted() {
    this.syncWorkbenchActiveTab()
  },
  methods: {
    showEnhancedConfirmDialog,
    syncWorkbenchActiveTab() {
      const visible = []
      if (this.showSubChannelManagementTab) visible.push('subChannelManagement')
      if (this.showUserManagementTab) visible.push('userManagement')
      if (this.$auth.hasSalesmanManagementPermission()) visible.push('salesmanManagement')
      if (this.showDataStatisticsTab) visible.push('otherManagement')
      if (visible.length && !visible.includes(this.activeTab)) {
        this.activeTab = visible[0]
      }
    },
    /**
     * 处理渠道码修改
     */
    handleChannelCodeModify() {
      this.channelCodeDialogVisible = true
    },
    /**
     * 渠道码修改成功回调
     */
    handleChannelCodeModifySuccess() {
      // 可以在这里添加修改成功后的操作，比如刷新数据等
      console.log('渠道码修改成功')
    },
    /** 
     * 处理业务员解绑
     */
    async handleUnbind() {
      try {
        const confirmed = await this.showEnhancedConfirmDialog(
          '确定要解绑业务员身份吗？解绑后将无法使用业务员相关功能。',
          '解绑确认',
          '确定解绑',
          '取消',
          { type: 'warning' }
        )
        if (!confirmed) return

        const response = await salesmanUnBundling()
        if (response && response.code === 200) {
          // 重新获取当前登录用户信息
          await this.$auth.refreshMainBody()
          ElMessage.success('解绑成功！')
          // 跳转到首页
          this.$router.push('/')
        }
      } catch (error) {
        console.error('解绑操作异常:', error)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.workbench-page {
  min-height: 100vh;
  padding-top: 84px; /* 为固定头部预留高度（与 header 一致） */
  background-color: #f5f7fa;
  display: flex;
  flex-direction: column;
}

.workbench-container {
  flex: 1;
  padding: 36px 24px 48px;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
}

.workbench-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid #ebeef5;
  overflow: hidden;

  .workbench-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 20px;
    padding: 0;
    background: linear-gradient(135deg, #fafbfc 0%, #f5f7fa 100%);
    border-bottom: 1px solid #ebeef5;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 4px;
      background: linear-gradient(180deg, #409eff 0%, #66b1ff 100%);
    }

    .workbench-header-left {
      display: flex;
      align-items: center;
      gap: 20px;
      padding: 28px 28px 28px 32px;
      flex: 1;
      min-width: 0;
    }

    .title-icon-wrap {
      width: 52px;
      height: 52px;
      border-radius: 12px;
      background: linear-gradient(135deg, #ecf5ff 0%, #d9ecff 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .title-icon {
      font-size: 28px;
      color: #409eff;
    }

    .title-block {
      min-width: 0;
    }

    .title-row {
      display: flex;
      align-items: center;
      gap: 10px;
      flex-wrap: wrap;
    }

    .workbench-title {
      margin: 0;
      font-size: 24px;
      font-weight: 700;
      color: #303133;
      letter-spacing: 0.02em;
      line-height: 1.3;

      .channel-org-name-prefix {
        font-weight: 600;
        color: #303133;
        margin-right: 6px;
      }

      .title-separator {
        font-weight: 600;
        color: #303133;
        margin-right: 8px;
      }
    }

    .role-badge {
      display: inline-block;
      padding: 2px 10px;
      font-size: 12px;
      font-weight: 600;
      color: #409eff;
      background: rgba(64, 158, 255, 0.1);
      border-radius: 20px;
      border: 1px solid rgba(64, 158, 255, 0.25);
    }

    .workbench-subtitle {
      margin: 8px 0 0;
      font-size: 14px;
      color: #909399;
      line-height: 1.4;
    }

    .workbench-header-actions {
      display: flex;
      align-items: center;
      gap: 14px;
      flex-shrink: 0;
      padding: 28px 28px;
      border-left: 1px solid #ebeef5;
    }

    .header-action-btn {
      height: 40px;
      padding: 0 20px;
      font-size: 14px;
      border-radius: 8px;
      display: inline-flex;
      align-items: center;
      gap: 6px;

      .btn-icon {
        font-size: 16px;
      }
    }

    .unbind-btn {
      color: #f56c6c;
      border-color: #fbc4c4;
      background: #fef0f0;

      &:hover {
        color: #fff;
        background: #f56c6c;
        border-color: #f56c6c;
      }

      .btn-icon {
        font-size: 15px;
      }
    }

    @media (max-width: 768px) {
      .workbench-header-left {
        padding: 22px 24px 22px 28px;
      }

      .workbench-header-actions {
        padding: 16px;
        border-left: none;
        border-top: 1px solid #ebeef5;
        width: 100%;
        box-sizing: border-box;
        justify-content: stretch;
        align-items: stretch;
        flex-wrap: wrap;
        gap: 10px;

        .header-action-btn {
          flex: 1 1 100%;
          min-width: 0;
          margin-left: 0 !important;
          justify-content: center;
        }
      }
    }

    @media (max-width: 640px) {
      flex-direction: column;
      align-items: stretch;
      gap: 0;

      .workbench-header-left {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
      }

      .workbench-title {
        font-size: 20px;
      }
    }
  }

  .workbench-disabled-tip {
    background: linear-gradient(180deg, #fefaf8 0%, #fff 100%);
    padding: 64px 40px;
    text-align: center;

    .disabled-tip-icon {
      color: #e6a23c;
      margin-bottom: 24px;
      line-height: 1;
    }

    .disabled-tip-title {
      margin: 0 0 14px;
      font-size: 19px;
      font-weight: 600;
      color: #303133;
    }

    .disabled-tip-desc {
      margin: 0;
      font-size: 14px;
      color: #909399;
      line-height: 1.6;
      max-width: 420px;
      margin-left: auto;
      margin-right: auto;
    }
  }

  .workbench-tabs {
    background: #fff;
    padding: 28px 24px 32px;

    :deep(.el-tabs__content) {
      width: 100%;
      overflow: visible;
    }

    :deep(.el-tab-pane) {
      width: 100%;
      min-width: 0;
    }

    :deep(.el-tabs__header) {
      margin: 0 0 24px;
    }

    :deep(.el-tabs__nav-wrap::after) {
      background-color: #e4e7ed;
    }

    :deep(.el-tabs__item) {
      font-size: 15px;
      font-weight: 500;
      color: #606266;
      padding: 0 28px;
      height: 58px;
      line-height: 58px;

      &.is-active {
        color: #1563e8;
        font-weight: 600;
      }

      &:hover {
        color: #1563e8;
      }
    }

    :deep(.el-tabs__active-bar) {
      height: 3px;
      background-color: #1563e8;
    }

    .empty-tab {
      padding: 48px 40px;
      text-align: center;
      color: #909399;
      font-size: 14px;
    }
  }
}

@media (max-width: 768px) {
  .workbench-page {
    padding-top: 64px;
    overflow-x: hidden;
  }

  .workbench-container {
    padding: 16px 12px 32px;
    min-width: 0;
  }

  .workbench-card {
    border-radius: 8px;

    .workbench-header {
      .workbench-title {
        word-break: break-word;

        .channel-org-name-prefix {
          word-break: break-all;
        }
      }
    }

    .workbench-disabled-tip {
      padding: 48px 20px;

      .disabled-tip-icon {
        margin-bottom: 16px;

        :deep(.el-icon) {
          font-size: 48px !important;
        }
      }

      .disabled-tip-title {
        font-size: 17px;
      }

      .disabled-tip-desc {
        font-size: 13px;
        max-width: 100%;
        padding: 0 8px;
      }
    }

    .workbench-tabs {
      padding: 16px 12px 20px;
      min-width: 0;

      :deep(.el-tabs__header) {
        margin: 0 0 16px;
      }

      :deep(.el-tabs__nav-wrap) {
        overflow: hidden;
        margin-bottom: 0;
      }

      :deep(.el-tabs__nav-scroll) {
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;

        &::-webkit-scrollbar {
          display: none;
        }
      }

      :deep(.el-tabs__nav) {
        float: none;
        display: inline-flex;
        white-space: nowrap;
      }

      :deep(.el-tabs__item) {
        flex-shrink: 0;
        padding: 0 16px;
        height: 44px;
        line-height: 44px;
        font-size: 14px;
      }

      :deep(.el-tabs__content) {
        overflow-x: auto;
      }

      .empty-tab {
        padding: 32px 16px;
      }
    }
  }
}

@media (max-width: 480px) {
  .workbench-container {
    padding: 12px 8px 24px;
  }

  .workbench-card {
    .workbench-header {
      .title-icon-wrap {
        width: 44px;
        height: 44px;
      }

      .title-icon {
        font-size: 24px;
      }

      .workbench-title {
        font-size: 18px;
      }

      .role-badge {
        font-size: 11px;
        padding: 2px 8px;
      }

      .workbench-subtitle {
        font-size: 13px;
      }

      .workbench-header-actions {
        flex-direction: column;
        align-items: stretch;
        gap: 10px;

        .header-action-btn {
          width: 100%;
          flex: none;
        }
      }
    }

    .workbench-tabs {
      padding: 12px 8px 16px;

      :deep(.el-tabs__item) {
        padding: 0 12px;
        height: 40px;
        line-height: 40px;
        font-size: 13px;
      }
    }
  }
}
</style>

